import { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { format, addDays } from 'date-fns';
import { CheckCircle2, ArrowRight, X, Battery, Zap, Moon, ChevronLeft, Check, ArrowRightCircle, XCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import CompletionButtons from './CompletionButtons';

export default function DailyCheckIn() {
  const { currentUser, plans, updateActivityStatus, updatePlan } = useFamilyContext();
  const [todaysPlan, setTodaysPlan] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const cameFromCalendar = searchParams.get('from') === 'calendar';
  
  const today = format(new Date(), 'yyyy-MM-dd');
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');

  useEffect(() => {
    if (currentUser) {
      const planKey = `${currentUser.id}_${today}`;
      const plan = plans[planKey] || [];
      setTodaysPlan(plan);
      setCompletedCount(plan.filter(a => a.status === 'completed').length);
    }
  }, [currentUser, plans, today]);

  const handleStatusUpdate = (activityIndex, newStatus) => {
    const activity = todaysPlan[activityIndex];
    
    // Update the status
    updateActivityStatus(currentUser.id, today, activity.activityId, newStatus);
    
    // If shifted, add to tomorrow's plan
    if (newStatus === 'shifted') {
      const tomorrowKey = `${currentUser.id}_${tomorrow}`;
      const tomorrowPlan = plans[tomorrowKey] || [];
      
      updatePlan(currentUser.id, tomorrow, [
        ...tomorrowPlan,
        {
          ...activity,
          status: 'planned',
          shiftedFrom: today
        }
      ]);
    }
    
    // Update local state
    const updatedPlan = [...todaysPlan];
    updatedPlan[activityIndex].status = newStatus;
    setTodaysPlan(updatedPlan);
    
    if (newStatus === 'completed') {
      setCompletedCount(prev => prev + 1);
    }
  };

  const getEnergyIcon = (level) => {
    switch(level) {
      case 'high': return <Zap className="w-5 h-5 text-red-500" />;
      case 'medium': return <Battery className="w-5 h-5 text-yellow-500" />;
      case 'low': return <Moon className="w-5 h-5 text-emerald_emerald-500" />;
      default: return null;
    }
  };

  const getTimeSlotActivities = (timeSlot) => {
    return todaysPlan.filter(activity => activity.timeSlot === timeSlot);
  };

  const allCompleted = todaysPlan.length > 0 && todaysPlan.every(a => a.status !== 'planned');

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-stone-900">Please log in</h2>
          <Link to="/" className="mt-4 inline-block px-6 py-2 bg-primary-600 text-neutral-50 rounded-lg hover:bg-primary-700">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-neutral-50 shadow-sm border-b sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link 
                to={cameFromCalendar ? "/family-calendar" : (currentUser?.originalParent ? "/parent-dashboard" : "/dashboard")} 
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-stone-600" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-stone-900">Today's Activities</h1>
                <p className="text-stone-600">{format(new Date(), 'EEEE, MMMM d')}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-stone-600">Progress</p>
              <p className="text-xl font-bold text-primary-600">{completedCount}/{todaysPlan.length}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 max-w-4xl mx-auto">
        {todaysPlan.length === 0 ? (
          <div className="bg-neutral-50 rounded-xl shadow-sm p-8 text-center">
            <p className="text-stone-600 mb-4">No activities planned for today</p>
            <Link
              to="/plan"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-neutral-50 font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Plan Activities
            </Link>
          </div>
        ) : (
          <>
            {allCompleted && (
              <div className="bg-emerald_emerald-50 border border-emerald_emerald-200 rounded-xl p-6 mb-6 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald_emerald-600 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-emerald_emerald-900 mb-2">Amazing job!</h2>
                <p className="text-emerald_emerald-700">You've handled all of today's activities. Enjoy your free time!</p>
              </div>
            )}

            {['intention1', 'intention2', 'intention3'].map(timeSlot => {
              const activities = getTimeSlotActivities(timeSlot);
              
              if (activities.length === 0) return null;
              
              return (
                <div key={timeSlot} className="mb-6">
                  <h2 className="text-lg font-semibold text-stone-900 mb-3 capitalize">
                    {timeSlot}
                  </h2>
                  
                  <div className="space-y-3">
                    {activities.map((activity, index) => {
                      const actualIndex = todaysPlan.findIndex(a => a === activity);
                      
                      return (
                        <div
                          key={index}
                          className={`bg-neutral-50 rounded-xl shadow-sm p-4 transition-all ${
                            activity.status === 'completed' ? 'opacity-60' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {getEnergyIcon(activity.energyLevel)}
                              <h3 className={`text-lg font-medium ${
                                activity.status === 'completed' ? 'line-through text-stone-500' : 'text-stone-900'
                              }`}>
                                {activity.activityName}
                              </h3>
                            </div>
                            {activity.shiftedFrom && (
                              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                                Shifted from yesterday
                              </span>
                            )}
                          </div>
                          
                          {activity.status === 'planned' ? (
                            <CompletionButtons
                              onComplete={() => handleStatusUpdate(actualIndex, 'completed')}
                              onShift={() => handleStatusUpdate(actualIndex, 'shifted')}
                              onCancel={() => handleStatusUpdate(actualIndex, 'cancelled')}
                            />
                          ) : (
                            <div className="text-center py-2">
                              <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                                activity.status === 'completed' ? 'bg-emerald_emerald-100 text-emerald_emerald-700' :
                                activity.status === 'shifted' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {activity.status === 'completed' ? (
                                  <span className="flex items-center gap-1">
                                    <Check className="w-4 h-4" /> Completed
                                  </span>
                                ) : activity.status === 'shifted' ? (
                                  <span className="flex items-center gap-1">
                                    <ArrowRightCircle className="w-4 h-4" /> Shifted to tomorrow
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <XCircle className="w-4 h-4" /> Cancelled
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </main>
    </div>
  );
}